import type { PsJob } from '../generated/models/ps-job';
import type { PsJobTimeSlot } from '../generated/models/ps-job-time-slot';
import type { PsJobListResponse } from '../generated/models/ps-job-list-response';
import type { UpQueueJob } from '../generated/models/up-queue-job';
import type { Job, MAEWStatus, TimeSlot, UpqueueResponse } from '../../models/job.model';
import type { AttemptRecord } from '../../models/attempt.model';
import { extractStatusMessage } from './adapter.utils';
import { maewFromCodes, parseAttemptCodes } from '../../services/maew.service';

/**
 * Extends the generated {@link PsJob} with fields returned by the live API but
 * not yet reflected in the OpenAPI spec.
 *
 * When the spec is updated and `npm run generate:api` includes these fields,
 * remove this extension and use `PsJob` directly.
 *
 * - `attempts` — array whose length equals attempt count; not in spec yet.
 * - `multidefendants` — live API returns `string[]`; the spec models it as
 *   `object[]` so we override the type here.
 * - `job_type` — raw job-type/header text (e.g. `"POSTING"`, `"Service of
 *   Process - RUSH Same Day"`); classified into {@link Job.jobTypeCategory}
 *   via `classifyJobType()`.
 */
type PsJobLive = PsJob & {
  attempts?: unknown[];
  /** Raw job-type/header text from the API. */
  job_type?: string;
  multidefendants?: string[];
  /** v2 API — non-zero when the work order was already printed server-side. */
  job_printed?: number | string;
  /** The client (law firm) that owns this job. Used for per-client feature flags. */
  client?: { id?: number };
};

/**
 * Maps the raw job list API response to an array of domain {@link Job} objects.
 *
 * @remarks
 * The API response is a Laravel paginated envelope typed as {@link PsJobListResponse}.
 * Using the generated type here means `npm run generate:api` keeps the input shape
 * in sync with the OpenAPI spec automatically — no manual `RawApiJob` interface needed.
 *
 * @param raw - Untyped response from `GET /process-server/{serverId}/jobs`.
 */
export function toModel(raw: unknown): Job[] {
  const list = (raw as PsJobListResponse).data ?? [];
  return list.map((j) => mapRawJob(j as PsJobLive));
}

/**
 * Returns `true` when the jobs-list response envelope declares itself a full
 * sync via `full_sync: "yes"` — present in `docs/openapispec.yml`'s v2
 * example alongside the Laravel pagination fields (`current_page`, `total`,
 * etc.), but not (yet) part of the generated `PsJobListResponse` type.
 *
 * @remarks
 * `JobsService.loadJobs()` uses this to decide full-replace vs. incremental
 * merge — **the server's own declaration takes priority over whether the
 * client itself sent a `lastsynctimestamp`**. This matters because a backend
 * can legitimately fall back to returning the complete list even when the
 * client requested an incremental diff (e.g. an expired/too-old timestamp) —
 * if the client still treated that response as incremental and merged it via
 * `mergeJobs()`, any job no longer present in that full list (reassigned
 * away, deleted, etc.) would never be removed from `openJobs`, since
 * `mergeJobs()` only adds/updates entries by ID and never deletes any.
 * Comparing against the exact string `"yes"` (not any truthy value) is
 * deliberate — only the one confirmed example value opts into full-replace;
 * an absent field (older backend/deployment) falls through to the existing
 * "did the client request a timestamp" behavior in `loadJobs()`.
 *
 * @param raw - Untyped response from `GET /process-server/{serverId}/jobs`.
 */
export function isFullSyncResponse(raw: unknown): boolean {
  return (raw as { full_sync?: unknown })?.full_sync === 'yes';
}

/**
 * Maps the `POST /process-server/{serverId}/jobs/upqueue` response.
 *
 * @remarks
 * Live API returns `{ status: "success", message: "Job(s) upqueued successfully!" }`.
 *
 * @param raw - Untyped API response.
 */
export function upqueueToModel(raw: unknown): UpqueueResponse {
  return extractStatusMessage(raw);
}

/**
 * Builds the `UpQueueJob` payload for
 * `POST /process-server/{serverId}/jobs/upqueue`.
 *
 * @param records - Array of completed attempt records from the UI.
 */
export function toPayload(records: AttemptRecord[]): UpQueueJob {
  return { jobs: records.map(mapAttemptRecord) };
}

/**
 * Maps a single {@link PsJob} (generated from OpenAPI spec) to the domain {@link Job} model.
 *
 * @remarks
 * All numeric flags (`job_status`, `job_accepted`, `job_onhold`, `job_approved_status`,
 * `job_manner`, `timesofday`) are coerced with `Number()` before comparison or assignment.
 * Laravel/PHP serialises integer flags as either `number` or `string` depending on context;
 * strict `=== 1` comparisons fail silently against `"1"` strings.
 * The generated `PsJob` type keeps this signature in sync with the OpenAPI spec —
 * run `npm run generate:api` after spec changes; no manual interface update needed here.
 *
 * @param raw - A single job entry from the Laravel-paginated `data[]` array.
 */
function mapRawJob(raw: PsJobLive): Job {
  const primaryName = raw.defendant?.name ?? '';
  // multidefendants is string[] in the live API (OpenAPI spec types it as object[]).
  const apiMultiNames = (raw.multidefendants ?? []).filter(Boolean) as string[];

  // The live API sometimes encodes multi-defendant names as 'ALICE AND BOB' in the
  // primary defendant name field with an empty multidefendants[] array. Parse them
  // out so defs and multi are always correct, and names display with '&' not 'AND'.
  const parsedPrimary: string[] = !apiMultiNames.length && primaryName.includes(' AND ')
    ? primaryName.split(' AND ').map((n) => n.trim()).filter(Boolean)
    : [primaryName].filter(Boolean);

  const defs = [...parsedPrimary, ...apiMultiNames].filter(Boolean) as string[];
  // The tokens are the source of truth: `maew` is derived from them, and they
  // are kept on the Job because the flags alone cannot say WHICH weekend slot
  // was attempted — which is what the weekend-repeat rule keys off.
  const attemptCodes = parseAttemptCodes(raw.timeofday ?? '');
  return {
    id:               String(raw.job_number),
    jobNum:           String(raw.job_number),
    defs,
    // service_documents[].document_id are the auth codes scanned/entered in VerifyComponent.
    // Positional order: [0] = primary defendant, [1..n] = multidefendants in order.
    // Empty strings are preserved (not filtered) so defendants without a document keep
    // their position — hasVerCode(i) treats '' as "no code → auto-verified".
    verCodes:         (raw.service_documents ?? [])
                        .map((d: { document_id?: number }) => String(d.document_id ?? '')),
    address:          raw.defendant?.address ?? '',
    slot:             resolveTimeSlot(raw.timeofday ?? ''),
    late:             isLate(raw.due_date),
    multi:            defs.length > 1,
    printed:          Number(raw.job_printed ?? 0) > 0,
    printCount:       Number(raw.job_printed ?? 0),
    special:          raw.service_instructions ?? '',
    maew:             maewFromCodes(attemptCodes),
    attemptCodes,
    scheduledSlots:   mapScheduledSlots(raw.timesofday ?? []),
    name:             defs[0] ?? '',
    plaintiff:        '',
    caseNum:          '',
    court:            '',
    documents:        '',
    jobStatus:        Number(raw.job_status          ?? 0),
    accepted:         Number(raw.job_accepted)        === 1,
    onHold:           Number(raw.job_onhold)          === 1,
    completionStatus: raw.completion_status           ?? '',
    esignStatus:      raw.job_esign_status            ?? '',
    approvedStatus:   Number(raw.job_approved_status  ?? 0),
    manner:           Number(raw.job_manner            ?? 0),
    dateAssigned:     raw.date_assigned                ?? '',
    attemptsMade:     raw.attempts?.length             ?? 0,
    jobType:          raw.job_type ?? '',
    jobTypeCategory:  classifyJobType(raw.job_type ?? ''),
    due:              formatDueDate(raw.due_date),
    clientId:         raw.client?.id ?? null,
  };
}

/**
 * Returns `true` when `due_date` represents a date strictly before today (local midnight).
 *
 * @remarks
 * Guards against the sentinel value `'0000-00-00'` returned by some API rows.
 * Compares against the start of today in local time (not UTC) so a job due today
 * is never considered late until midnight local time.
 *
 * @param due_date - ISO date string from the API (e.g. `"2024-03-15"`), `'0000-00-00'`, or empty.
 */
function isLate(due_date: string | undefined): boolean {
  if (!due_date || due_date === '0000-00-00') return false;
  const due   = new Date(due_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return !isNaN(due.getTime()) && due < today;
}

/**
 * Converts an ISO `YYYY-MM-DD` due-date string from the API into `MM/DD/YYYY`
 * display format. Returns `''` for the sentinel `'0000-00-00'`, empty, or
 * unparseable values.
 *
 * @param raw - ISO date string from the API (e.g. `"2026-06-24"`), `'0000-00-00'`, or empty.
 */
function formatDueDate(raw: string | undefined): string {
  if (!raw || raw === '0000-00-00') return '';
  const [y, m, d] = raw.split('-');
  if (!y || !m || !d) return '';
  return `${m}/${d}/${y}`;
}

/**
 * Classifies a raw `job_type` string into one of the three pill states shown
 * in the job card footer.
 *
 * @remarks
 * Any value containing "rush" (case-insensitive) is classified `'Rush'` —
 * this covers every active/inactive "...RUSH..." header variant (Same Day,
 * 2-7 days, 8-20 days, SINGLE ATTEMPT, plain RUSH). An exact match of
 * `"POSTING"` (case-insensitive) is classified `'Posting'`. Everything else
 * — including `"POST & MAIL"`, `"Delivery"`, `"OFFICE"`, `"File"`,
 * `"2ATT Service of Process"`, `"SkipTrace"`, and `"Service of Process -
 * Routine"` itself — defaults to `'Routine'`. `'Posting'` and `'Routine'`
 * render with identical grey pill styling; only `'Rush'` gets the red pill.
 *
 * @param jobType - Raw `job_type` value from the API (`raw.job_type`), or `''`.
 */
function classifyJobType(jobType: string): 'Rush' | 'Posting' | 'Routine' {
  const normalized = jobType.toLowerCase();
  if (normalized.includes('rush')) return 'Rush';
  if (normalized === 'posting') return 'Posting';
  return 'Routine';
}

/**
 * Maps a `timeofday` string to the primary {@link TimeSlot} display label.
 *
 * @remarks
 * The API sends a concatenated run of tokens (e.g. `"MWm"`). The first
 * recognised token determines the primary slot label shown in the UI.
 * Token map: `M`→Morning, `A`→Afternoon, `E`→Evening, `W`→Weekend,
 * `Wm`→Morning, `Wa`→Afternoon, `We`→Evening.
 *
 * @param timeofday - Raw `timeofday` value from the API (`raw.timeofday`).
 */
function resolveTimeSlot(timeofday: string): TimeSlot {
  const slotMap: Record<string, TimeSlot> = {
    M: 'Morning', A: 'Afternoon', E: 'Evening', W: 'Weekend',
    Wm: 'Morning', Wa: 'Afternoon', We: 'Evening',
  };
  const [first] = parseAttemptCodes(timeofday);
  return first ? (slotMap[first] ?? 'Morning') : 'Morning';
}

/**
 * Derives the MAEW scheduled-slot flags from the `timesofday[]` array.
/**
 * Derives the MAEW scheduled-slot flags from the `timesofday[]` array.
 *
 * @remarks
 * Each entry's `timesofday` flag (`1`/`0`) indicates whether the slot is
 * **scheduled/assigned** for this job on the server side. A slot is `true` when its
 * array entry has `timesofday === 1`. Weekend sub-tokens (`Wm`/`We`/`Wa`) in the
 * `code` field set both the time-of-day slot AND `W`.
 * An empty array defaults all slots to `true` (no scheduling constraint).
 *
 * @param slots - Raw `timesofday[]` array from the API.
 */
function mapScheduledSlots(slots: PsJobTimeSlot[]): MAEWStatus {
  if (slots.length === 0) return { M: true, A: true, E: true, W: true };
  const scheduled = new Set(
    slots
      .filter(s => Number(s.timesofday) === 1)
      .map(s => String(s.code ?? ''))
      .filter(Boolean),
  );
  return {
    M: scheduled.has('M')  || scheduled.has('Wm'),
    A: scheduled.has('A')  || scheduled.has('Wa'),
    E: scheduled.has('E')  || scheduled.has('We'),
    W: scheduled.has('W')  || scheduled.has('Wm') || scheduled.has('We') || scheduled.has('Wa'),
  };
}

/**
 * Extends the generated `UpQueueJob` jobs-array element with per-defendant
 * not-served reasons accepted by the API but not yet in the OpenAPI spec.
 */
type UpQueueJobItem = NonNullable<UpQueueJob['jobs']>[number] & {
  not_served_defendants?: { name: string; reason: string | null }[];
};

/**
 * Maps a domain `AttemptRecord` to the snake_case API shape expected by
 * `POST /process-server/{serverId}/jobs/upqueue`.
 *
 * @param r - Domain attempt record produced by `AttemptLogComponent`.
 */
function mapAttemptRecord(r: AttemptRecord): UpQueueJobItem {
  return {
    job_number: r.jobNumber,
    upload_photo: r.photos.map((p) => ({
      upload_photo_name: p.name,
      upload_photo_source: p.source,
      upload_photo_latitude: p.latitude,
      upload_photo_longitude: p.longitude,
      upload_photo_time: p.takenAt,
    })),
    job_completion_status: r.completionStatus,
    job_completion_date: r.completionDate,
    job_completion_time: r.completionTime,
    spokewith_relationship: r.spokeWithRelationship,
    spokewith_relationship_other: r.spokeWithRelationshipOther,
    spokewith_firstname: r.spokeWithFirstName,
    gender: r.gender,
    skin_color: r.skinColor,
    hair_color: r.hairColor,
    age: r.age,
    height: r.height,
    weight: r.weight,
    additional_hygiene_factor: r.additionalHygieneFactor,
    married: r.married,
    military: r.military,
    observations: r.observations,
    observation_information: r.observationInformation,
    comments: r.comments,
    completion_type: r.completionType,
    completion_type_id: r.completionTypeId,
    bad_address_reason: r.badAddressReason,
    bad_address_reason_other: r.badAddressReasonOther,
    leftwith_relationship: r.leftWithRelationship,
    leftwith_relationship_other: r.leftWithRelationshipOther,
    leftwith_firstname: r.leftWithFirstName,
    not_served_defendants: r.notServedDefendants,
  };
}
